import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import PublicLayout from "@/layouts/PublicLayout";
import { api } from "@/lib/api";
import { CheckCircle2, Download, Loader2, XCircle } from "lucide-react";
import { useLang } from "@/context/LangContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function EventPaymentSuccess() {
  const { t } = useLang();
  const { slug } = useParams();
  const [sp] = useSearchParams();
  const sessionId = sp.get("session_id");
  const regId = sp.get("reg_id");

  const [status, setStatus] = useState("polling"); // polling | paid | timeout | error
  const [result, setResult] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId || !regId) { setStatus("error"); return; }
    let cancelled = false;
    let attempt = 0;

    const poll = async () => {
      if (cancelled) return;
      attempt += 1;
      setAttempts(attempt);
      try {
        const { data } = await api.get(`/event-registrations/${regId}/payment-status/${sessionId}`);
        if (data.payment_status === "paid") {
          setResult(data);
          setStatus("paid");
          return;
        }
        if (["expired", "failed", "superseded"].includes(data.payment_status)) {
          setStatus("error");
          return;
        }
      } catch (e) { /* keep polling */ }
      if (attempt >= 10) { setStatus("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, regId]);

  return (
    <PublicLayout>
      <section className="mx-auto max-w-2xl px-6 py-20">
        <div className="rounded-3xl border border-wine-100 bg-white p-10 text-center shadow-sm" data-testid="event-payment-success">
          {status === "polling" && (
            <>
              <Loader2 className="mx-auto h-12 w-12 animate-spin text-wine-500" />
              <h1 className="mt-6 font-serif text-3xl text-navy">{t("comp_eventpaymentsuccess_90")}</h1>
              <p className="mt-2 text-sm text-neutral-600">Tentative {attempts}/10 — merci de patienter.</p>
            </>
          )}
          {status === "paid" && (
            <>
              <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-500" />
              <h1 className="mt-6 font-serif text-4xl text-navy">{t("comp_eventpaymentsuccess_91")}</h1>
              <p className="mt-3 text-neutral-700">{t("comp_eventpaymentsuccess_92")}</p>
              {result?.badge_code && (
                <div className="mt-4 inline-block rounded-xl border border-wine-200 bg-wine-50 px-4 py-2 font-mono text-sm font-bold text-wine-700">{result.badge_code}</div>
              )}
              <div className="mt-6 flex flex-wrap justify-center gap-3">
                <Link to={`/portal/event/${slug}/overview`} className="inline-flex items-center gap-2 rounded-full bg-wine-500 px-6 py-3 text-sm font-semibold text-white hover:bg-wine-600" data-testid="go-my-event">
                  Accéder à mon espace événement
                </Link>
                <a
                  href={`${BACKEND}/api/registrations/${regId}/badge`}
                  target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-2 rounded-full border border-wine-300 px-6 py-3 text-sm font-semibold text-wine-700 hover:bg-wine-50"
                  data-testid="badge-download"
                >
                  <Download className="h-4 w-4" /> Télécharger mon badge PDF
                </a>
              </div>
            </>
          )}
          {status === "timeout" && (
            <>
              <Loader2 className="mx-auto h-12 w-12 text-amber-500" />
              <h1 className="mt-6 font-serif text-3xl text-navy">Paiement en traitement</h1>
              <p className="mt-3 text-sm text-neutral-600">{t("comp_eventpaymentsuccess_93")}</p>
              <Link to={`/events/${slug}`} className="mt-6 inline-block rounded-full bg-wine-500 px-6 py-3 text-sm font-semibold text-white hover:bg-wine-600">{t("comp_eventpaymentsuccess_94")}</Link>
            </>
          )}
          {status === "error" && (
            <>
              <XCircle className="mx-auto h-12 w-12 text-red-500" />
              <h1 className="mt-6 font-serif text-3xl text-navy">{t("comp_eventpaymentsuccess_95")}</h1>
              <p className="mt-3 text-sm text-neutral-600">{t("comp_eventpaymentsuccess_96")}</p>
              <Link to={`/events/${slug}`} className="mt-6 inline-block rounded-full bg-wine-500 px-6 py-3 text-sm font-semibold text-white hover:bg-wine-600">{t("comp_eventpaymentsuccess_94")}</Link>
            </>
          )}
        </div>
      </section>
    </PublicLayout>
  );
}
